// Value formatting that mirrors insight-bi's display logic so the native
// renderers (Cards, Feeds, Tree, Grid, Pivot) show the same text the Angular
// components do:
//   • numbers  → buildNumericFormats: IND/USA/EURO locale + decimal places
//                derived from a FORMAT mask (e.g. "IND.##", "999.99").
//   • dates    → an Angular-DatePipe-style pattern (default "dd-MMM-yy").
// These are intentionally dependency-free.

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]
const DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** A FORMAT value that means "render an icon image", not a number mask. */
export function isIconFormat(format?: string | null): boolean {
  return !!format && /^icon\b/i.test(String(format).trim())
}

/** Locale insight-bi maps a FORMAT prefix to (IND→en-IN, USA→en-US, EURO→en-IE). */
function localeFromFormat(format?: string | null): string | undefined {
  if (!format) return undefined
  const f = String(format).toUpperCase()
  if (f.includes('IND')) return 'en-IN'
  if (f.includes('USA')) return 'en-US'
  if (f.includes('EURO')) return 'en-IE'
  return undefined
}

/** Decimal places taken from the part after "." in a FORMAT mask (count of #/9/0). */
function decimalsFromFormat(format?: string | null): number | undefined {
  if (!format) return undefined
  const s = String(format)
  const dot = s.indexOf('.')
  if (dot < 0) return undefined
  const frac = s.slice(dot + 1).replace(/[^#90]/g, '')
  return frac.length
}

/** Format a numeric value honouring an insight-bi FORMAT mask. */
export function formatNumber(value: unknown, format?: string | null): string {
  if (value == null || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const locale = localeFromFormat(format)
  const decimals = decimalsFromFormat(format)
  const opts: Intl.NumberFormatOptions =
    decimals != null
      ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
      : { maximumFractionDigits: 4 }
  try {
    return n.toLocaleString(locale, opts)
  } catch {
    return n.toLocaleString(undefined, opts)
  }
}

/** Format a date with an Angular-DatePipe-style pattern (subset). */
// True when a date value carries a nonzero time component. For strings the time
// is read directly from the text (`…T14:05` / `… 14:05:00`) so a date-only value
// parsed as UTC midnight isn't misread as having a time in a non-UTC viewer.
function dateValueHasNonzeroTime(value: unknown, d: Date): boolean {
  if (value instanceof Date) {
    return !!(d.getHours() || d.getMinutes() || d.getSeconds())
  }
  const m = String(value).match(/[T ](\d{2}):(\d{2})(?::(\d{2}))?/)
  if (!m) return false
  return Number(m[1]) !== 0 || Number(m[2]) !== 0 || Number(m[3] || 0) !== 0
}

export function formatDate(value: unknown, pattern?: string | null): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  // With no explicit pattern, default to a date OR datetime pattern depending
  // on whether the source value carries a nonzero time component — so a
  // timestamp keeps its HH:mm (grids/cards/feeds/pivot used to silently drop
  // the time) while a plain date stays date-only. Read the time from the raw
  // string to avoid a timezone-parse shift misclassifying a date-only value.
  const pat = pattern && String(pattern).trim()
    ? String(pattern)
    : (dateValueHasNonzeroTime(value, d) ? 'dd-MMM-yy HH:mm' : 'dd-MMM-yy')
  const pad = (n: number) => String(n).padStart(2, '0')
  const h12 = d.getHours() % 12 || 12
  const tokens: Record<string, string> = {
    yyyy: String(d.getFullYear()),
    yy: String(d.getFullYear()).slice(-2),
    MMMM: MONTHS_LONG[d.getMonth()],
    MMM: MONTHS_SHORT[d.getMonth()],
    MM: pad(d.getMonth() + 1),
    M: String(d.getMonth() + 1),
    dd: pad(d.getDate()),
    d: String(d.getDate()),
    EEEE: DAYS_LONG[d.getDay()],
    EEE: DAYS_SHORT[d.getDay()],
    HH: pad(d.getHours()),
    H: String(d.getHours()),
    hh: pad(h12),
    h: String(h12),
    mm: pad(d.getMinutes()),
    ss: pad(d.getSeconds()),
    a: d.getHours() < 12 ? 'AM' : 'PM',
  }
  // Longer tokens first so e.g. MMMM matches before MMM/MM/M.
  return pat.replace(/yyyy|yy|MMMM|MMM|MM|M|dd|d|EEEE|EEE|HH|H|hh|h|mm|ss|a/g, (t) => tokens[t] ?? t)
}

/**
 * Synthesize a ProPivot/DisplayFormat descriptor from a legacy FORMAT / PATTERN
 * mask so a column that only carries the old masks still formats richly in the
 * pivot/grid (which consume `display`). Number masks (IND/USA/EURO + decimal
 * places) → { type:'number', locale, decimals }; date columns → { type:'date',
 * datePattern }. Returns undefined when there's nothing to synthesize.
 * `display` set by the author always wins; this is only the legacy fallback.
 */
export function maskToDisplay(
  format?: string | null,
  pattern?: string | null,
  coltype?: string | null,
): Record<string, unknown> | undefined {
  const isDateCol = coltype === 'DATE'
  const isNumberCol = coltype === 'NUMBER'
  if (isDateCol || (pattern && !isNumberCol && !format)) {
    const dp = pattern && String(pattern).trim() ? String(pattern) : undefined
    return { type: 'date', ...(dp ? { datePattern: dp } : {}) }
  }
  if (format && !isIconFormat(format) && /IND|USA|EURO|[#90]/.test(format)) {
    const locale = localeFromFormat(format)
    const decimals = decimalsFromFormat(format)
    return {
      type: 'number',
      ...(locale ? { locale } : {}),
      ...(decimals != null ? { decimals } : {}),
    }
  }
  return undefined
}

/**
 * Inverse of maskToDisplay: derive a legacy FORMAT / PATTERN mask from a
 * number/date DisplayFormat so the vendored charts (which format axis ticks /
 * tooltips from FORMAT masks, not `display`) honour a column authored purely
 * with `display`. Rich display types (data bars, tags, …) have no mask and are
 * table-only — they return {}.
 */
export function displayToMask(
  display: Record<string, unknown> | undefined,
): { FORMAT?: string; PATTERN?: string } {
  const type = display && typeof display === 'object' ? String(display.type ?? '') : ''
  if (type === 'number') {
    const locale = String((display as { locale?: unknown }).locale ?? '')
    const localeToken = locale === 'en-IN' ? 'IND' : locale === 'en-US' ? 'USA' : locale === 'en-IE' ? 'EURO' : ''
    const decRaw = (display as { decimals?: unknown }).decimals
    const decimals = typeof decRaw === 'number' && decRaw >= 0 ? decRaw : undefined
    const mask = localeToken + (decimals != null ? '.' + '0'.repeat(decimals) : '')
    return mask ? { FORMAT: mask } : {}
  }
  if (type === 'date') {
    const dp = (display as { datePattern?: unknown }).datePattern
    return dp ? { PATTERN: String(dp) } : {}
  }
  return {}
}

export interface FormatHints {
  type?: string        // schema type: 'number' | 'datetime' | 'string'
  coltype?: string     // ColumnGroup COLTYPE: 'NUMBER' | 'DATE' | 'CHAR' | 'T'
  format?: string      // FORMAT mask (IND/USA/EURO/9-mask/Icon)
  pattern?: string     // DATE pattern
}

const isDate = (h: FormatHints) =>
  h.coltype === 'DATE' || h.type === 'datetime' || h.type === 'date'
const isNumber = (h: FormatHints) =>
  h.coltype === 'NUMBER' || h.type === 'number'

/** Format a single value using the column's hints (date → number → text). */
export function formatCell(value: unknown, hints: FormatHints = {}): string {
  if (value == null) return ''
  if (isDate(hints)) return formatDate(value, hints.pattern)
  if (isNumber(hints) || (hints.format && /IND|USA|EURO|[#90]/.test(hints.format))) {
    if (typeof value === 'number' || !Number.isNaN(Number(value))) {
      return formatNumber(value, hints.format)
    }
  }
  return String(value)
}
